import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux' // Combined imports
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import store from 'store' // Make sure this library is robust or consider alternatives if needed

import eyeLine from 'assets/icon/eye-line.svg'
import eyeOffLine from 'assets/icon/eye-off-line.svg'
import greenCheckmarkFlat from 'assets/icon/green-checkmark-flat.svg'

import Input from 'components/UI/Input'
import Radio from 'components/UI/Radio'
import Checkbox from 'components/UI/Checkbox'
import Button from 'components/UI/Button'
import ErrorMessageWithExclamation from 'components/UI/ErrorMessageWithExclamation'
import NotChangeableFieldLabel from '../../Shared/NotChangeableFieldLabel'

import {
  register as registerAction,
  setStep,
} from '../../../routing/fetchingfunction/user/user.action'
import { useNavigate } from 'react-router-dom'
import {
  selectIsLoading,
  selectErrorsCreateAccount,
} from 'routing/fetchingfunction/user/user.reducer'

import { createAccountSchema } from '../../../routing/utils/validators/creatAccountSchema'

import { displayNameOptions, TUTOR_ROLE, BACKEND_REGISTRATION_CODES } from '../../../constants'
import NameVerificationModal from 'pages/common/settings/Tabs/NameVerificationModal'

// --- Error Handling Improvements ---
// 1. Added try...catch around store.set
// 2. Added aria-describedby to link inputs to errors
// 3. Added role="alert" and aria-live="assertive" to dynamic error messages (server & checkbox)
// ---------------------------------

const CreateAccount = ({ role = 'student' }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const isLoading = useSelector(selectIsLoading)
  const createAccountError = useSelector(selectErrorsCreateAccount)

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      display_name: 'nickname',
    },
    resolver: yupResolver(createAccountSchema),
    // Optionally set mode to 'onTouched' or 'onChange' for earlier validation feedback
    // mode: 'onTouched'
  })

  const [showPassword, setShowPassword] = useState(false)
  const [isCheckbox1Checked, setIsCheckbox1Checked] = useState(false)
  const [isCheckbox2Checked, setIsCheckbox2Checked] = useState(false)
  const [isCheckbox3Checked, setIsCheckbox3Checked] = useState(false)
  const [areAllCheckboxesChecked, setAreAllCheckboxesChecked] = useState(null) // null | true | false
  const [formData, setFormData] = useState(null)

  const [showModal, setShowModal] = useState(false)

  const password = watch('password')
  const isTutor = role === TUTOR_ROLE

  const validationItems = [
    { validation: 'hasMinLength', label: 'Min. 8 Characters', value: password?.length >= 8 },
    {
      validation: 'hasUppercase',
      label: 'At least one uppercase letter [A-Z]',
      value: /[A-Z]/.test(password || ''),
    },
    {
      validation: 'hasLowercase',
      label: 'At least one lowercase letter [a-z]',
      value: /[a-z]/.test(password || ''),
    },
    {
      validation: 'hasNumber',
      label: 'At least one number [0-9]',
      value: /[0-9]/.test(password || ''),
    },

    {
      validation: 'hasSpecialChar',
      label: 'At least one special character (e.g., @, #, $, %, etc.)',
      value: /[!@#$%^&*(),.?":{}|<>]/.test(password || ''),
    },
    {
      validation: 'hasNoSpace',
      label: 'No spaces allowed',
      value: /^(?=\S+$).+/.test(password || ''),
    }, // No spaces allowed
  ]

  // Removed the useEffect that was watching for registration completion
  // We'll handle the navigation directly in the form submission

  const handleFormSubmit = async (data) => {
    // Reset checkbox error state on new submission attempt
    setAreAllCheckboxesChecked(null)

    if (!isCheckbox1Checked || !isCheckbox2Checked || !isCheckbox3Checked) {
      setAreAllCheckboxesChecked(false) // Set state to show error
      return // Prevent submission
    }

    setAreAllCheckboxesChecked(true) // Checkboxes are okay

    // Show loading state
    const submitButton = document.querySelector('button[type="submit"]')
    const originalButtonText = submitButton?.textContent
    if (submitButton) {
      submitButton.disabled = true
      submitButton.textContent = 'Creating account...'
    }

    // Clear any existing registration data in localStorage
    try {
      // Clear any existing stored tokens or registration state
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('register_step')
      localStorage.removeItem('last_registration_step')
      sessionStorage.removeItem('register_step')

      // Clear all stored data related to registration
      store.remove('access_token')
      store.remove('refresh_token')
      store.remove('register_step')
      store.remove('user_data')

      // Set fresh display_name
      store.set('display_name', data.display_name)

      console.log('Cleared existing registration data for fresh start')
    } catch (error) {
      console.error('Error managing local storage during registration:', error)
    }

    const registrationData = {
      user_type: role,
      sendData: {
        first_name: data.first_name,
        last_name: data.last_name,
        username: data.username,
        email: data.email,
        password: data.password,
        display_name: data.display_name,
      },
    }

    console.log('Submitting registration data:', registrationData)
    try {
      const response = await dispatch(registerAction(registrationData)).unwrap()
      console.log('Registration successful in component', response)

      // The register action should have set the step to email_verification in Redux
      // Now we'll navigate to the email verification step
      navigate(`/${role}/register/email-verification`, { replace: true })
    } catch (error) {
      console.error('Registration failed in CreateAccount component:', error)
      // Re-enable the submit button
      const submitButton = document.querySelector('button[type="submit"]')
      if (submitButton) {
        submitButton.disabled = false
        submitButton.textContent = 'Create Account'
      }

      // The error will be displayed by the error handling in the component
    }
  }

  const handleCreateClick = handleSubmit((data) => {
    if (!isCheckbox1Checked || !isCheckbox2Checked || !isCheckbox3Checked) {
      setAreAllCheckboxesChecked(false)
      return
    }

    setFormData(data)
    setAreAllCheckboxesChecked(true)
    if (isTutor) {
      setShowModal(true)
    } else {
      handleFormSubmit(data)
    }
  })

  const handleToggleShowPassword = () => {
    setShowPassword(!showPassword)
  }

  // Generate unique IDs for error messages
  const firstNameErrorId = 'first_name_error'
  const lastNameErrorId = 'last_name_error'
  const usernameErrorId = 'username_error'
  const emailErrorId = 'email_error'
  const passwordErrorId = 'password_error'
  const checkboxErrorId = 'checkbox_error'
  const serverErrorId = 'server_error'
  const usernameServerErrorId = 'username_error_server' // New ID for server username error
  const emailServerErrorId = 'email_error_server' // New ID for server email error

  // Helper function to extract a general error message for the top display
  const getGeneralServerErrorMessage = (error) => {
    if (!error) return null
    if (typeof error === 'string') return error // If error is already a string
    if (error.detail && typeof error.detail === 'string') return error.detail // For errors like { detail: "message" }
    // If it's an object of field errors (e.g., { username: [...], email: [...] }) and no 'detail' key,
    // we don't show a general message at the top, as field errors are shown below.
    // If there are other unexpected top-level keys, or it's an object without known field keys or detail,
    // you might want a generic fallback.
    const knownFieldKeys = [
      'username',
      'email',
      'password',
      'first_name',
      'last_name' /* add other known field error keys */,
    ]
    const errorKeys = Object.keys(error)
    const isOnlyFieldErrors = errorKeys.every((key) => knownFieldKeys.includes(key))
    if (errorKeys.length > 0 && !isOnlyFieldErrors && !error.detail) {
      // If there are keys other than known field keys and no detail, show a generic message
      // Or concatenate them if they are strings: Object.values(error).join(', ')
      return 'An unexpected error occurred. Please check the details below or try again.'
    }
    return null // No general message if only known field errors or specific unhandled object structure
  }

  const generalServerErrorMessage = getGeneralServerErrorMessage(createAccountError)

  return (
    // Consider adding aria-labelledby="form-title" to the form if the h3 has an id="form-title"
    <form
      onSubmit={handleCreateClick}
      className="bg-[#FEEBDD] rounded-lg py-16 px-10 sm:px-20 md:px-50 lg:px-100 xl:px-[148px] text-[#F67443]  max-w-[400] lg:max-w-[800px]   md:max-w-[700px]
  sm:max-w-[600px]
 w-full mx-auto my-16 border border-red-500
"
    >
      <h3 id="form-title" className="text-4xl font-semibold text-center">
        Create an Account
      </h3>
      {role === TUTOR_ROLE && (
        <p className="max-w-md mx-auto text-[#f79009] font-normal text-center my-10">
          For security reasons, please provide your real name and information to pass the KYC.
        </p>
      )}

      {/* Server Error Display for non-field specific errors */}
      {generalServerErrorMessage && (
        <div
          id={serverErrorId}
          role="alert"
          aria-live="assertive"
          className="mt-4 mb-6 p-3 border border-[#F67443] bg-[#FEEBDD] rounded-md text-[#F67443]"
        >
          <div className="flex flex-col gap-2">
            <p className="font-bold">Registration Error</p>
            <ErrorMessageWithExclamation text={generalServerErrorMessage} />
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-col items-start justify-start w-full">
        {/* First Name */}
        <div className="text-sm font-medium !mb-4 w-full">
          <NotChangeableFieldLabel htmlFor="first_name">First Name</NotChangeableFieldLabel>
          <Input
            id="first_name"
            wrapperClassName="w-full"
            className="w-full"
            type="text"
            placeholder="First Name"
            aria-invalid={!!errors.first_name} // Indicate invalid state
            aria-describedby={errors.first_name ? firstNameErrorId : undefined} // Link to error message
            {...register('first_name')}
          />
          {errors.first_name && (
            <div id={firstNameErrorId}>
              {' '}
              {/* Ensure ID matches aria-describedby */}
              <ErrorMessageWithExclamation text={errors.first_name?.message} />
            </div>
          )}
        </div>

        {/* Last Name */}
        <div className="text-sm font-medium !mb-4 w-full">
          <NotChangeableFieldLabel htmlFor="last_name">Last Name</NotChangeableFieldLabel>
          <Input
            id="last_name"
            wrapperClassName="w-full"
            className="w-full"
            type="text"
            placeholder="Last Name"
            aria-invalid={!!errors.last_name}
            aria-describedby={errors.last_name ? lastNameErrorId : undefined}
            {...register('last_name')}
          />
          {errors.last_name && (
            <div id={lastNameErrorId}>
              <ErrorMessageWithExclamation text={errors.last_name?.message} />
            </div>
          )}
          <span className="my-1.5 text-[#f79009] text-xs">
            Your real name is hidden by default.{' '}
          </span>
        </div>

        {/* Nickname (Username) */}
        <div className="text-sm font-medium mb-4 w-full">
          {/* Ensure label htmlFor matches input id/name if possible, here it's 'username' */}
          <NotChangeableFieldLabel htmlFor="nickname">Nickname</NotChangeableFieldLabel>
          <Input
            id="nickname" // Keep id consistent with label if possible, but register uses 'username'
            wrapperClassName="w-full"
            className="w-full"
            type="text"
            placeholder="Nickname"
            aria-invalid={
              !!errors.username ||
              !!(
                createAccountError &&
                typeof createAccountError === 'object' &&
                createAccountError.username
              )
            }
            aria-describedby={
              [
                errors.username ? usernameErrorId : null,
                createAccountError &&
                typeof createAccountError === 'object' &&
                createAccountError.username
                  ? usernameServerErrorId
                  : null,
              ]
                .filter(Boolean)
                .join(' ') || undefined
            }
            {...register('username')} // Registered field name is 'username'
          />
          {errors.username && (
            <div id={usernameErrorId}>
              <ErrorMessageWithExclamation text={errors.username?.message} />
            </div>
          )}
          {/* Server-side validation error for username */}
          {createAccountError &&
            typeof createAccountError === 'object' &&
            createAccountError.username && (
              <div id={usernameServerErrorId} role="alert">
                <ErrorMessageWithExclamation
                  text={
                    Array.isArray(createAccountError.username)
                      ? createAccountError.username[0]
                      : createAccountError.username
                  }
                />
              </div>
            )}
          {/* Display Name Options */}
          <span className="my-1.5 text-[#f79009] text-xs">
            Your nickname name is visible by default.
          </span>
        </div>

        {/* Consider wrapping Radio group in fieldset with legend for accessibility */}
        {/* <fieldset> <legend>Choose how your name is displayed</legend> ... </fieldset> */}
        <div className="w-full max-w-full  !mb-5">
          <div className="w-full grid grid-cols-2 gap-x-4">
            {displayNameOptions.map((displayNameOption) => (
              <Radio
                key={displayNameOption.value}
                // Add unique ID for each radio if needed for labels
                wrapperClassName="border !border-primary-450 rounded-lg bg-white py-2 px-1 sm:py-3 sm:px-4 hover:cursor-pointer overflow-hidden"
                labelClassName="text-xs sm:text-sm font-medium break-words"
                label={displayNameOption.label}
                value={displayNameOption.value}
                {...register('display_name')} // Validation is likely handled by the schema (required?)
              />
            ))}
          </div>
          {/* Add error display for display_name if it's required in the schema */}
          {/* {errors.display_name && <ErrorMessage... /> } */}
        </div>

        {/* Email */}
        <div className="text-sm font-medium !mb-4 w-full">
          <NotChangeableFieldLabel htmlFor="email">Email</NotChangeableFieldLabel>
          <Input
            id="email"
            wrapperClassName="w-full"
            className="w-full"
            type="email"
            placeholder="Enter your email here"
            aria-invalid={
              !!errors.email ||
              !!(
                createAccountError &&
                typeof createAccountError === 'object' &&
                createAccountError.email
              )
            }
            aria-describedby={
              [
                errors.email ? emailErrorId : null,
                createAccountError &&
                typeof createAccountError === 'object' &&
                createAccountError.email
                  ? emailServerErrorId
                  : null,
              ]
                .filter(Boolean)
                .join(' ') || undefined
            }
            {...register('email')}
          />
          {errors.email && (
            <div id={emailErrorId}>
              <ErrorMessageWithExclamation text={errors.email?.message} />
            </div>
          )}
          {/* Server-side validation error for email */}
          {createAccountError &&
            typeof createAccountError === 'object' &&
            createAccountError.email && (
              <div id={emailServerErrorId} role="alert">
                <ErrorMessageWithExclamation
                  text={
                    Array.isArray(createAccountError.email)
                      ? createAccountError.email[0]
                      : createAccountError.email
                  }
                />
              </div>
            )}
        </div>

        {/* Password */}
        <div className="text-sm font-medium w-full !mb-6">
          {/* Use htmlFor for label */}
          <label htmlFor="password">Password</label>
          <div className="w-full relative">
            <Input
              id="password" // Added ID
              wrapperClassName="w-full"
              className="w-full"
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              aria-invalid={!!errors.password}
              aria-describedby={errors.password ? passwordErrorId : undefined}
              {...register('password')}
            />
            {/* Consider making the toggle button a proper <button> for accessibility */}
            <button
              type="button" // Prevent form submission
              className="absolute top-1/2 right-[14px] -translate-y-1/2 hover:cursor-pointer p-1 border-none bg-transparent" // Added styling resets
              onClick={handleToggleShowPassword}
              aria-label={showPassword ? 'Hide password' : 'Show password'} // Accessibility label
            >
              <img
                className="block w-5 h-5"
                src={showPassword ? eyeOffLine : eyeLine}
                alt=""
                aria-hidden="true"
              />{' '}
              {/* Alt can be empty if button has aria-label */}
            </button>
          </div>

          {/* Password Requirements List */}
          <div className="text-xs space-y-1 mt-1">
            {/* Requirement 1: Min 8 characters */}
            {validationItems.map((item, index) => (
              <div className={`flex items-center  'text-primary-75`}>
                <div className="w-5 mr-1 flex justify-center">
                  {item.value && (
                    <img
                      className="w-[13px] h-[13px] block"
                      src={greenCheckmarkFlat}
                      alt="Requirement met"
                    />
                  )}
                </div>
                <span className="text-xs text-[#F79009] font-normal">{item.label}</span>
              </div>
            ))}
          </div>

          {/* Password Validation Error */}
          {errors.password && (
            // Wrap message in a block element like div or p for structure
            <div id={passwordErrorId} className="mt-1">
              <ErrorMessageWithExclamation text={errors.password?.message} />
            </div>
          )}
        </div>

        {/* Checkboxes */}
        {/* Consider wrapping Checkboxes in a fieldset for grouping */}
        {/* <fieldset> <legend>Agreements</legend> ... </fieldset> */}
        <div className="mb-6 pl-0  -mr-2 -pr-2 ">
          <Checkbox
            // Add id and aria-describedby if validation error specific to this checkbox exists
            label={`I'm over ${isTutor ? '18' : '18'} years old`}
            labelClassName="text-sm font-medium my-2"
            checked={isCheckbox1Checked}
            onChange={() => setIsCheckbox1Checked(!isCheckbox1Checked)}
          />
          <Checkbox
            wrapperClassName="!items-start my-1"
            className="!top-1"
            checked={isCheckbox2Checked}
            onChange={() => setIsCheckbox2Checked(!isCheckbox2Checked)}
            label={
              <div className="text-sm">
                I agree with the&nbsp;
                <Link
                  className="font-bold hover:underline underline hover:text-[#f67443]"
                  to="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </Link>
                ,&nbsp;
                <Link
                  className="font-bold underline hover:text-[#f67443]"
                  to="/copyright-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Copywriting Policy
                </Link>
                ,&nbsp;
                <Link
                  className="font-bold text-sm underline inline-block hover:text-[#f67443]"
                  to="/cookie-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Cookie Policy,
                </Link>
                &nbsp;
                <Link
                  className="font-bold underline hover:text-[#f67443]"
                  to="/cancellation-and-refund-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Cancellation & Refund Policy
                </Link>
                .
              </div>
            }
            labelClassName="flex-1 text-sm font-medium"
            // Add rel="noopener noreferrer" to target="_blank" links for security
          />
          <Checkbox
            wrapperClassName="!items-start"
            className="!top-1"
            checked={isCheckbox3Checked}
            onChange={() => setIsCheckbox3Checked(!isCheckbox3Checked)}
            label={
              <span className="text-sm">
                I agree with the&nbsp;
                <Link
                  className="font-bold underline hover:text-[#f67443]"
                  to="/disclaimer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Disclaimer
                </Link>
                ,&nbsp;
                <Link
                  className="font-bold underline hover:text-[#f67443]"
                  to="/community-guidelines"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Community Guidelines
                </Link>
                ,&nbsp;
                <Link
                  className="font-bold underline hover:text-[#f67443]"
                  to={isTutor ? '/tutor-terms-of-use' : '/terms-of-use'}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {isTutor && 'Tutor '}Terms of Use
                </Link>
              </span>
            }
            labelClassName="flex-1 text-sm font-medium"
          />
          {/* Checkbox Validation Error */}
          {areAllCheckboxesChecked === false && (
            // Added role and aria-live for announcement
            <div id={checkboxErrorId} role="alert" aria-live="assertive" className="mt-1">
              <ErrorMessageWithExclamation text="Please accept all terms and policies to continue." />{' '}
              {/* Slightly clearer message */}
            </div>
          )}
        </div>

        {/* Submit Button */}
        <Button
          className="w-full !text-[#FDFDFD] !font-semibold !border-[#C37104] border  shadow "
          type="submit"
          onClick={handleCreateClick}
          isLoading={isLoading}
          // Optionally disable button if checkboxes aren't checked or form is invalid
          // disabled={isLoading || !isCheckbox1Checked || !isCheckbox2Checked || !isCheckbox3Checked}
          // Or even: disabled={isLoading || !isValid || !isCheckbox1Checked || !isCheckbox2Checked || !isCheckbox3Checked} (if using formState.isValid)
        >
          Create Account
        </Button>

        {/* Modal */}
        {isTutor && (
          <NameVerificationModal
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            onContinue={() => {
              if (formData) {
                setShowModal(false)
                handleFormSubmit(formData)
              }
            }}
          />
        )}
      </div>
    </form>
  )
}

export default CreateAccount
